import { SelectCards } from 'components/Form/SelectCards';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'twin.macro';
import SimpleNarrowPage from 'components/Layouts/SimpleNarrowPage';
import { useTranslation } from 'react-i18next';
import FadeIn from 'components/Transitions/FadeIn';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { BoListLarge } from 'components/Bo/BoListLarge';
import useBo from 'queries/bo/useBo';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useCreateProgrammation } from 'containers/Programmations/atoms/useCreateProgrammation';
import { ProgModeles } from 'containers/Programmations/ProgV2/New/ProgModeles';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import Tooltip2 from 'components/Tooltip2';
import { buildPeriodes } from './Footer';
import { trackEvent } from 'queries/user/track';
import useClassrooms from 'containers/App/context/ClassroomsContext/useClassrooms';
import { getSchoolyearWeeks } from 'containers/Programmations/ProgV2/utils/getSchoolyearWeeks';
import { useProgrammations } from 'containers/Programmations/atoms/useProgrammations';
import { AlreadyAProgCallout } from './AlreadyAProgCallout';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import H1 from 'components/Typography/Headings/H1';
import { useSchoolGoTo } from '@teetsh/app/src/containers/App/useSchoolGoTo';

interface Props {
  isProgression: boolean;
}

export function NewProgrammation(props: Props) {
  const { isProgression } = props;
  const { t } = useTranslation(['programmation', 'common']);
  const userPref = useUser();
  const { schoolyear, school } = useSchoolSchoolyear();
  const classrooms = useClassrooms();
  const defaultCycle = classrooms.flatMap(c => c.cycle)[0];
  const [cycle, setCycle] = useState<string>(defaultCycle);
  const goTo = useSchoolGoTo();
  const navigate = useNavigate();
  const createProgrammation = useCreateProgrammation();
  const [selectedBo, setSelectedBo] = useState<string>(userPref.referencielId);
  const [selectedMatiere, setSelectedMatiere] = useState<string | undefined>(
    '',
  );
  const { data: allProgrammations = [] } = useProgrammations();

  const { data: bo } = useBo(selectedBo);
  const matchingCycle = bo?.cycles.find(c => c.nom === cycle);
  const matchingMatiere = matchingCycle?.matieres.find(
    m => m.nom === selectedMatiere,
  );

  useEffect(() => {
    if (matchingMatiere?.nom !== selectedMatiere) {
      setSelectedMatiere(undefined);
    }
  }, [bo, cycle]);

  // If the selected BO doesn't have the cycle, set it to the first one
  useEffect(() => {
    if (bo && cycle) {
      const cycleExistsInBo = bo.cycles.some(c => c.nom === cycle);
      if (!cycleExistsInBo && bo.cycles.length > 0) {
        setCycle(bo.cycles[0].nom);
      }
    }
  }, [bo, selectedBo]);

  const goToProg = (id: string) =>
    navigate(
      `/annee-scolaire/${schoolyear.id}/ecole/${school.id}/programmations/${id}`,
    );
  const progType = isProgression
    ? t('programmation:onboarding.progression')
    : t('programmation:onboarding.programmation');

  const schoolyearWeeks = getSchoolyearWeeks({
    zone: schoolyear.zone,
    weekLabelFt: n => t('programmation:weekX', { count: n }),
    sundayStart: false,
  });

  const progressionsForThatCycle = cycle
    ? allProgrammations.filter(
        p => p.cycle === cycle && p.view === 'week' && isProgression,
      )
    : [];
  const programmationForThatCycle = cycle
    ? allProgrammations.filter(
        p => p.cycle === cycle && p.view === 'periode' && !isProgression,
      )
    : [];

  return (
    <div>
      <div tw="mb-6 pt-2 pb-4 w-full inline-flex items-center justify-between">
        <Button
          onClick={() => goTo('programmations/new')}
          minimal
          intent="none"
          icon={ArrowLeftIcon}
          aria-label="Retour"
        />
        <H1 tw="pr-9 grow text-center lg:display">
          {t('programmation:onboarding.configureProg', { progType })}
        </H1>
      </div>
      <SimpleNarrowPage>
        <div tw="space-y-10 pt-8">
          <div tw="flex flex-col gap-y-2">
            <div tw="text-lg font-medium flex gap-x-2 items-center">
              {t('programmation:onboarding.selectReferentiel')}{' '}
              <Tooltip2
                label={
                  <div tw="max-w-lg text-wrap">
                    {t('programmation:onboarding.selectReferentielTooltip')}
                  </div>
                }
              >
                <QuestionMarkCircleIcon tw="h-5 w-5" />
              </Tooltip2>
            </div>
            <BoListLarge
              onChange={boId => {
                setSelectedBo(boId);
              }}
              value={selectedBo}
            />
          </div>

          <div>
            <div tw="text-lg font-medium mb-4">
              {isProgression
                ? t('programmation:onboarding.progression.selectCycle')
                : t('programmation:onboarding.programmation.selectCycle')}
            </div>
            <div>
              <SelectCards
                options={
                  (bo?.cycles || []).map(c => ({
                    value: c.nom,
                    description: c.nom,
                  })) || []
                }
                value={cycle}
                onChange={v => setCycle(v)}
              />
            </div>
          </div>
          {(progressionsForThatCycle || []).length > 0 && (
            <AlreadyAProgCallout
              title={t('programmation:existingProgressionTitle')}
              description={t('programmation:existingProgressionDescription')}
              path={`programmations/${progressionsForThatCycle[0].id}`}
              progName={progressionsForThatCycle[0].name}
              ctaLink={t('programmation:existingProgressionLink')}
            />
          )}
          {(programmationForThatCycle || []).length > 0 && (
            <AlreadyAProgCallout
              title={t('programmation:existingProgrammationTitle')}
              description={t('programmation:existingProgrammationDescription')}
              path={`programmations/${programmationForThatCycle[0].id}`}
              progName={programmationForThatCycle[0].name}
              ctaLink={t('programmation:existingProgrammationLink')}
            />
          )}

          {cycle && (
            <FadeIn>
              <div>
                <div tw="text-lg mb-4 font-medium">
                  {t('programmation:onboarding.selectLearningArea')}
                </div>
                <div tw="mb-12">
                  <SelectCards
                    enableCustomOption
                    options={
                      (bo?.cycles || [])
                        .find(c => c.nom === cycle)
                        ?.matieres.map(m => ({
                          value: m.nom,
                          description: m.nom,
                        })) || []
                    }
                    value={selectedMatiere}
                    onChange={v => setSelectedMatiere(v)}
                  />
                </div>
              </div>
            </FadeIn>
          )}
        </div>

        {selectedMatiere && cycle && (
          <FadeIn>
            <ProgModeles
              matiere={matchingMatiere || { nom: selectedMatiere }}
              isProgression={isProgression}
              cycle={cycle}
              onSelect={id => {
                createProgrammation({
                  template: {
                    templateId: id,
                    periodes: isProgression
                      ? schoolyearWeeks
                      : buildPeriodes(schoolyear.zone?.periodes || []),
                  },
                }).then(prog => {
                  trackEvent('Programmation:startFromTemplate', {
                    view: props.isProgression ? 'week' : 'periode',
                    cycle,
                    templateId: id.toString(),
                    matiere: matchingMatiere?.nom || selectedMatiere,
                  });
                  return goToProg(prog?.id);
                });
              }}
            />
          </FadeIn>
        )}
      </SimpleNarrowPage>
    </div>
  );
}
